<template>
    <div class="view-report-static-header">
        <div class="view-report-static-header__info">
            <div class="view-report-static-header__info__title">
                <ChevronLeft
                    class="view-report-static-header__info__title-icon"
                    @click="onBackClick"
                />
                <span>{{ pageTitle }}</span>
            </div>
            <span
                v-if="pageSubtitle"
                class="view-report-static-header__info__subtitle"
            >
                {{ pageSubtitle }}
            </span>
        </div>
        <div class="view-report-static-header__export">
            <pbl-button v-if="userPermissions.canExportGeneratorReports" @click="onExportClick">
                {{ t('reports.detailsPage.actions.export') }}
            </pbl-button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ChevronLeft } from 'lucide-vue-next'
import { computed, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePermissions } from '@/composables/usePermissions'
import { useRoute, useRouter } from 'vue-router'
import { ReportTypes } from '@/configuration/report/report-types'
import { format } from 'date-fns'
import { DATE_FORMAT } from '@/utils/date-utils'
import { useGeneratorDetailsQuery } from '@/api/queries/generators/useGeneratorDetailsQuery'
import { ComponentType } from '@/configuration/component/component-types'
import { useGeneratorsReportDetailsExportStateStore } from '@/store/reports/export/generators/exportReportStateStore'
import { RouteName } from '@/configuration/route/route-names'
import { goBackInAppOr } from '@/utils/router-navigation-utils'
import { useGeneratorReportGetQuery } from '@/api/queries/reports/useGeneratorReportGetQuery'

const props = defineProps<{
    reportNumber: string
    reportType: number
    generatorId: string
}>()

const { reportNumber, reportType, generatorId } = toRefs(props)

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { userPermissions } = usePermissions()

const generatorsReportDetailsExportStateStore = useGeneratorsReportDetailsExportStateStore()

const { data: generatorDetailsData } = useGeneratorDetailsQuery(generatorId)
const { data } = useGeneratorReportGetQuery(reportNumber, generatorId)

const pageTitle = computed(() => {
    if (route.name === RouteName.GeneratorsReportsViewCondition) {
        return t('reports.detailsPage.pageTitle.reportTitle.conditionStatus', {
            componentId: generatorId.value ?? '-',
            reportNumber: reportNumber.value,
        })
    }

    return t('reports.detailsPage.pageTitle.reportTitle.default', {
        reportNumber: reportNumber.value,
    })
})

const reportDate = computed(() => data.value?.reports?.at(0)?.date)

const pageSubtitle = computed(() => {
    if (!generatorDetailsData.value || !data.value) {
        return ''
    }

    const plantGroupName = generatorDetailsData.value.riverGroupName
    const plantName = generatorDetailsData.value.powerPlantName
    const generatorName = generatorDetailsData.value.name
    const generatorSid = generatorDetailsData.value.generatorID
    const reportTypeString = t(ReportTypes.find((e) => e.value === reportType.value)?.labelKey ?? '-')

    const reportDateFormatted = format(new Date(reportDate.value ?? new Date()), DATE_FORMAT)

    return `${plantGroupName} / ${plantName} / ${generatorName} / ${generatorSid} / ${reportTypeString} / ${reportDateFormatted}`
})

const onExportClick = () => {
    router.push({
        name: 'Export_ViewReport',
        params: {
            componentSid: generatorId.value,
            componentType: ComponentType.Generator.toString(),
            reportNumber: reportNumber.value,
            reportType: reportType.value.toString(),
        },
        query: generatorsReportDetailsExportStateStore.reportDetailsExportParameters,
    })
}

const onBackClick = () => {
    goBackInAppOr(router, () => {
        const id = generatorId.value
        if (id) {
            router.push({
                name: 'Generators_ConditionStatus',
                params: { generatorId: id },
            })
            return
        }
        router.push({ name: 'Generators_PortfolioOverview' })
    })
}
</script>

<style scoped lang="scss">
.view-report-static-header {
    @include spacing-page;

    display: flex;
    justify-content: space-between;
    align-items: center;

    background-color: $pbl-foreground;
    border-bottom: 1px solid $pbl-border-muted;

    &__info {
        display: flex;
        flex-direction: column;
        gap: $spacing-s;

        &__title {
            display: flex;
            align-items: center;

            gap: $spacing-2xs;

            &-icon {
                margin-bottom: -2px;
                height: 20px;
                width: 20px;

                cursor: pointer;
            }

            span {
                font-size: 1.25rem;
                font-weight: 500;
            }
        }
    }

    &__export {
        margin-right: 2rem;
    }
}
</style>
